/**
 * What moved on a fleet record, in the words the ledger keeps it in.
 *
 * "מי ביצע את השינוי" is only answerable if the change itself was written down
 * legibly, and legibly means *now*: a row that stored `status: "grounded"` would
 * read as a raw enum for as long as the ledger lives, and a row that stored only
 * the record id would need the record to still exist. So the diff is taken here,
 * against the two versions the store is holding anyway, and both sides are
 * spelled in Hebrew before they are filed.
 *
 * The module is pure — no database, no clock beyond what the caller passes — so
 * the healthcheck exercises the real comparison rather than a copy of it. The
 * writing itself is `recordFleetChange()` in `src/db/audit-store.ts`; the two
 * callers are `updateVehicle()` and `updateSoldier()` in
 * `src/db/fleet-store.ts`, and nothing else may diff a fleet record.
 */
import { soldierRoleLabel } from './soldier-roles';
import { formatStatusHorizon } from './soldier-status';
import { vehicleCallSign } from './military-id';
import {
  SOLDIER_AVAILABILITY_LABELS,
  TIMED_STATUS_LABELS,
  VEHICLE_CATEGORY_LABELS,
  VEHICLE_PERMIT_LABELS,
  VEHICLE_STATUS_LABELS,
  VEHICLE_TYPE_LABELS,
  WEIGHT_CATEGORY_LABELS,
} from '../types/fleet';
import type { Soldier, TimedStatus, Vehicle } from '../types/fleet';
import type { FieldChange } from '../types/audit';

/** Reads a value that may legitimately be missing as the dash the ledger shows. */
const plain = (value: unknown): string => {
  const text = String(value ?? '').trim();
  return text || '—';
};

/** Looks a code up in its label table, falling back to the code itself. */
const labelled = (table: Record<string, string>, value: unknown): string =>
  table[String(value ?? '')] ?? plain(value);

/**
 * A timed status as one sentence — "בבית עד מחר 08:00", "בגימלים עד להודעה
 * חדשה", or "ללא סטטוס זמני" when it was cleared.
 *
 * The horizon is spelled by `src/core/soldier-status.ts`, which owns that
 * sentence; this only chooses the words either side of it.
 */
export function describeTimedStatusValue(
  status: TimedStatus | null | undefined,
  now: number = Date.now(),
): string {
  if (!status?.kind) return 'ללא סטטוס זמני';
  const note = status.note?.trim() ? ` · ${status.note.trim()}` : '';
  return `${TIMED_STATUS_LABELS[status.kind]} ${formatStatusHorizon(status.until, now)}${note}`;
}

/** How each vehicle field is named and read in the ledger. */
const VEHICLE_FIELDS: readonly {
  field: keyof Vehicle;
  label: string;
  read: (vehicle: Vehicle) => string;
}[] = [
  {
    field: 'status',
    label: 'מצב תפעולי',
    read: (v) => labelled(VEHICLE_STATUS_LABELS, v.status),
  },
  { field: 'assignedCompany', label: 'מחזיקה', read: (v) => plain(v.assignedCompany) },
  { field: 'militaryId', label: 'מספר פנימי', read: (v) => plain(v.militaryId) },
  { field: 'plateNumber', label: 'מספר צ', read: (v) => plain(v.plateNumber) },
  { field: 'name', label: 'שם', read: (v) => plain(v.name) },
  { field: 'model', label: 'דגם', read: (v) => plain(v.model) },
  {
    field: 'vehicleType',
    label: 'סוג רכב',
    read: (v) => labelled(VEHICLE_TYPE_LABELS, v.vehicleType),
  },
  {
    field: 'weightCategory',
    label: 'דרגת משקל',
    read: (v) => labelled(WEIGHT_CATEGORY_LABELS, v.weightCategory),
  },
  {
    field: 'category',
    label: 'מאגר',
    read: (v) => labelled(VEHICLE_CATEGORY_LABELS, v.category),
  },
  { field: 'company', label: 'יחידת אם', read: (v) => plain(v.company) },
  { field: 'notes', label: 'הערות', read: (v) => plain(v.notes) },
];

/** How each soldier field is named and read in the ledger. */
const SOLDIER_FIELDS: readonly {
  field: keyof Soldier;
  label: string;
  read: (soldier: Soldier, now: number) => string;
}[] = [
  {
    field: 'availability',
    label: 'זמינות',
    read: (s) => labelled(SOLDIER_AVAILABILITY_LABELS, s.availability),
  },
  {
    field: 'temporaryStatus',
    label: 'סטטוס',
    read: (s, now) => describeTimedStatusValue(s.temporaryStatus, now),
  },
  { field: 'fullName', label: 'שם מלא', read: (s) => plain(s.fullName) },
  { field: 'role', label: 'תפקיד', read: (s) => soldierRoleLabel(s.role) },
  { field: 'personalNumber', label: 'מספר אישי', read: (s) => plain(s.personalNumber) },
  { field: 'phone', label: 'טלפון', read: (s) => plain(s.phone) },
  { field: 'company', label: 'פלוגה', read: (s) => plain(s.company) },
  { field: 'platoon', label: 'מחלקה', read: (s) => plain(s.platoon) },
  {
    field: 'licenseWeightCategory',
    label: 'דרגת רישיון',
    read: (s) =>
      s.licenseWeightCategory
        ? labelled(WEIGHT_CATEGORY_LABELS, s.licenseWeightCategory)
        : 'ללא רישיון צבאי',
  },
  {
    field: 'licensePermits',
    label: 'הרשאות תפעול',
    read: (s) =>
      (s.licensePermits ?? []).map((permit) => labelled(VEHICLE_PERMIT_LABELS, permit)).join(', ') ||
      '—',
  },
  { field: 'notes', label: 'הערות', read: (s) => plain(s.notes) },
];

/**
 * True when two stored values are the same as far as the ledger is concerned.
 *
 * Compared structurally, because a status object and a permit list are rewritten
 * wholesale on every save: a manager who re-saved a form without touching it
 * hands the store a brand-new object holding the same values, and that is not a
 * change anybody wants to read about.
 */
const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a ?? null) === (b ?? null);
  }
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
};

/** Runs one field table over a before/after pair. */
function diff<T>(
  table: readonly { field: keyof T; label: string; read: (record: T, now: number) => string }[],
  before: T,
  after: T,
  now: number,
): FieldChange[] {
  const moved: FieldChange[] = [];
  for (const entry of table) {
    if (sameValue(before[entry.field], after[entry.field])) continue;
    const from = entry.read(before, now);
    const to = entry.read(after, now);
    // Two different stored values that read as the same sentence are not worth
    // a line — an empty string becoming `null` is not news.
    if (from === to) continue;
    moved.push({ field: String(entry.field), label: entry.label, from, to });
  }
  return moved;
}

/** Every vehicle field that moved between the two versions, in table order. */
export function diffVehicle(
  before: Vehicle,
  after: Vehicle,
  now: number = Date.now(),
): FieldChange[] {
  return diff(VEHICLE_FIELDS, before, after, now);
}

/** Every soldier field that moved between the two versions, in table order. */
export function diffSoldier(
  before: Soldier,
  after: Soldier,
  now: number = Date.now(),
): FieldChange[] {
  return diff(SOLDIER_FIELDS, before, after, now);
}

/** How a vehicle is named in its own history — `350 · מסחרית`. */
export const vehicleChangeLabel = (vehicle: Vehicle): string =>
  `${vehicleCallSign(vehicle)} · ${vehicle.name || vehicle.model}`;

/** How a soldier is named in their own history — the name they are filed under. */
export const soldierChangeLabel = (soldier: Soldier): string => soldier.fullName;
